// src/components/steps/HospitalsListStep.js

import React, { useState, useEffect, useRef } from "react";
import { fetchHospitalsNearby } from "../../services/api"; 
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const HospitalsListStep = ({ cep, onCount }) => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    if (!cep || cep.length < 8) return;

    const fetchList = async () => {
      setLoading(true);
      setError(null);
      try {
        // --- LÓGICA CORRIGIDA ---
        // 1. A API é chamada e retorna uma lista direta de HOSPITAIS.
        const hospitalsData = await fetchHospitalsNearby(cep);
        
        // 2. Os dados recebidos são usados diretamente para definir o estado.
        if (Array.isArray(hospitalsData)) {
          setHospitals(hospitalsData);
          onCount(hospitalsData.length > 0);
        } else {
          // Se a API não retornar um array, a lista fica vazia.
          setHospitals([]);
          onCount(false);
        }

      } catch (e) {
        console.error("Erro ao buscar dados:", e);
        setError("Erro ao buscar hospitais. Tente novamente.");
        setHospitals([]);
        onCount(false);
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, [cep, onCount]);

  // O resto do seu componente (a parte visual) permanece igual...

  if (loading) {
    return (
      <div className="flex justify-center mt-6">
        <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2A4DD0] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <p className="text-red-500 text-center mt-6">{error}</p>;
  }

  if (!hospitals.length) {
    return (
      <div className="text-center mt-6">
        <p className="text-gray-600 mb-4">
          Desculpe-nos, não encontramos unidades no CEP informado.
        </p>
        <p className="text-gray-600 mb-2">
          Deseja ajuda para encontrar um plano disponível para sua região?
        </p>
        <a
          href="https://wa.me/5511988887777?text=Olá!%20Não%20encontrei%20unidades%20no%20meu%20CEP.%20Gostaria%20de%20ajuda%20para%20escolher%20um%20plano%20de%20saúde."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-2 px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
        >
          Falar com um consultor no WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div className="mt-6 w-full">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Hospitais próximos</h3>
      <div className="relative px-12">
        <div ref={prevRef} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600 hover:text-blue-800" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </div>
        <div ref={nextRef} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600 hover:text-blue-800" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </div>
        
        <Swiper
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          slidesPerView={1}
          spaceBetween={10}
          loop={hospitals.length > 1}
        >
          {hospitals.map((hospital) => (
            <SwiperSlide key={hospital.id}>
              <div className="bg-white rounded-xl shadow-lg flex flex-col h-full p-8 max-w-md mx-auto">
                <img src={hospital.logoUrl || "/alleanza-logo.png"} alt="Logo do Plano" className="h-8 w-auto object-contain mx-auto mb-6" />
                <h4 className="text-xl font-bold text-slate-800 text-center mb-6">{hospital.name}</h4>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start text-left">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    <span className="text-slate-600">{hospital.adress}</span>
                  </div>
                  <div className="flex items-start text-left">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    <span className="text-slate-600">{hospital.type}</span>
                  </div>
                </div>
                <div className="text-center mt-auto">
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hospital.adress)}`} target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                    Ver no mapa
                  </a>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default HospitalsListStep;